import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { Select, Spin } from "antd";
import GetNotifcationsApi from "../../api/notifications/GetNotificationsApi";
import OpenNotificationApi from "../../api/notifications/OpenNotificationApi";
import Theme from "../../theme/Theme";

const { Option } = Select;

function RecentActivities({ handleNotificationCount }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState("all");
  const positionState = useSelector((state) => state.positionState.value);
  const navigate = useNavigate();

  useEffect(() => {
    if (positionState?.id) {
      loadNotifications();
    }
  }, [positionState?.id, filter]);

  async function loadNotifications() {
    setLoading(true);
    try {
      const apiResponse = await GetNotifcationsApi(
        1,
        10, // Limit to 10 recent activities
        "",
        positionState.id
      );

      if (apiResponse.success) {
        let notifications = apiResponse.data?.notifications || [];
        
        // Apply filter
        if (filter !== "all") {
          notifications = notifications.filter((item) => {
            const message = item?.notification?.message?.toLowerCase() || "";
            
            switch (filter) {
              case "requests":
                return message.includes("approval") || message.includes("request");
              case "designation":
                return message.includes("designation");
              case "unit":
                return message.includes("unit");
              default:
                return true;
            }
          });
        }
        
        setData(notifications);
      }
    } catch (error) {
      console.error("Error loading notifications:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }

  async function openNotification(id, url) {
    await OpenNotificationApi(id);
    if (handleNotificationCount) {
      handleNotificationCount();
    }
    if (url) {
      navigate(url);
    }
  }

  // Helper function to parse notification message and extract user name and action
  function parseNotificationMessage(message) {
    if (!message) return { userName: null, action: message };
    
    // Common patterns in notification messages:
    // "John Doe replied to your comment"
    // "John Doe commented on plan"
    // "John Doe updated KPI targets"
    // "Plan XYZ Created"
    // etc.
    
    // Try to extract user name (first part before common action verbs)
    const actionVerbs = [
      'replied to',
      'commented on',
      'updated',
      'created',
      'approved',
      'declined',
      'requested',
      'completed',
      'added',
      'removed',
      'changed',
      'assigned',
    ];
    
    for (const verb of actionVerbs) {
      const index = message.toLowerCase().indexOf(verb);
      if (index > 0) {
        const userName = message.substring(0, index).trim();
        const action = message.substring(index).trim();
        return { userName, action };
      }
    }
    
    // If no pattern matched, return the whole message as action
    return { userName: null, action: message };
  }

  return (
    <div 
      className="mb-8 p-6 rounded-lg"
      style={{
        backgroundColor: 'white',
      }}
    >
      <div className="flex justify-between items-center mb-4">
        <div
          className="text-lg font-semibold"
          style={{
            color: Theme().textPrimary,
            fontFamily: "Inter, sans-serif",
          }}
        >
          Recent Activities
        </div>
        <Select
          value={filter}
          onChange={setFilter}
          style={{ 
            width: 150,
            borderRadius: '6px',
          }}
          bordered={true}
        >
          <Option value="all">All Activities</Option>
          <Option value="requests">Requests</Option>
          <Option value="designation">Designation</Option>
          <Option value="unit">Unit</Option>
        </Select>
      </div>

      <div
        style={{
          maxHeight: "400px",
          overflowY: "auto",
        }}
      >
        {loading ? (
          <div className="text-center py-8">
            <Spin />
          </div>
        ) : data.length === 0 ? (
          <div
            className="text-center py-8"
            style={{
              color: Theme().textPrimary,
              opacity: 0.6,
              fontFamily: "Inter, sans-serif",
            }}
          >
            No activities found
          </div>
        ) : (
          <div className="space-y-2">
            {data.map((item) => {
              const { userName, action } = parseNotificationMessage(item?.notification?.message);
              
              return (
                <div
                  key={item.id}
                  onClick={() => openNotification(item.id, item.notification?.url)}
                  className="cursor-pointer p-3 rounded hover:shadow-sm transition-shadow"
                  style={{
                    backgroundColor: item.status === "UnRead" ? "#f5f5f5" : "transparent",
                  }}
                >
                  <div className="flex items-center gap-2 text-xs mb-1" style={{ color: Theme().textPrimary, opacity: 0.6 }}>
                    <div>{moment(item?.notification?.createdOn).format("DD.MM.YYYY")}</div>
                    <div>{moment(item?.notification?.createdOn).format("HH:mm a")}</div>
                  </div>
                  <div
                    className="text-sm"
                    style={{
                      color: Theme().textPrimary,
                      fontFamily: "Inter, sans-serif",
                      fontWeight: item.status === "UnRead" ? 500 : 400,
                    }}
                  >
                    {userName ? (
                      <>
                        <span style={{ fontWeight: 600, color: Theme().primary }}>
                          {userName}
                        </span>
                        {' '}
                        <span>{action}</span>
                      </>
                    ) : (
                      <span>{action}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {!loading && data.length > 0 && (
          <div className="text-center mt-4">
            <span
              onClick={() => window.dispatchEvent(new Event("open-notifications"))}
              className="text-sm hover:opacity-80 cursor-pointer"
              style={{
                color: Theme().primary,
                fontFamily: "Inter, sans-serif",
              }}
            >
              Load More
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default RecentActivities;
